import type { RuntimeKind, RuntimeCapabilities } from './types';

/**
 * THE capability table (PLAN §4.2).
 *
 * A literal `Record<RuntimeKind, RuntimeCapabilities>` — four rows, every cell
 * explicit, no computed defaults, no spreads, no inheritance. Reviewers read
 * the table; a behavioural change is a one-line visible diff.
 *
 * The `ios`, `android` and `web` rows encode **today's shipped behaviour
 * exactly**. They are a regression fence, not an aspiration: every cell is
 * pinned by `__tests__/capabilities.test.ts` against the predicate the app
 * uses today, so a desktop change can never quietly move mobile or web.
 *
 * The `electron` row is PLAN §3 with `manualCardEntry: 'none'` per AMENDMENT
 * A2 (Manual Card is Stripe-specific and Stripe is out of desktop scope), and
 * Phase W's `blockchypLocalNetwork: true` (BlockChyp LAN terminals from the
 * main process — docs/electron-phase1/BLOCKCHYP_WINDOWS_FINDINGS.md).
 */
export const RUNTIME_CAPABILITIES: Record<RuntimeKind, RuntimeCapabilities> = {
  ios: {
    // identity & auth
    nativeFirebaseAuth: true,
    firebaseWebApp: 'named-web-services',
    firebaseAuthInit: 'initialize-auth-indexeddb',
    deviceRegistrationType: 'POS_DEVICE',
    loginPairingShortcuts: true,
    // storage
    secureStorage: 'keychain',
    localDatabase: 'sqlite-native',
    // payments
    manualCardEntry: 'payment-sheet',
    blockchypLocalNetwork: true,
    rendererBlockChypConsole: true,
    blockchypCloudProbe: true,
    stripeM2: true,
    localTerminalPing: true,
    completedReportPlatform: 'IOS',
    desktopDispatchReconcile: false,
    // printing
    thermalPrinting: 'star-native',
    bluetoothPeripherals: true,
    desktopPrintDialog: false,
    escPosLanPrinting: false,
    escPosAttachedPrinting: false,
    orderAudio: 'native',
    // platform services
    osPermissionPrompts: true,
    otaUpdates: true,
    appVersionSource: 'capacitor-native',
    remoteVersionPolicy: true,
    deviceDiagnostics: true,
    batteryTelemetry: true,
    wifiManagement: 'ios-settings',
    inAppBrowser: 'capacitor-browser',
  },

  android: {
    // identity & auth
    nativeFirebaseAuth: true,
    firebaseWebApp: 'named-web-services',
    firebaseAuthInit: 'initialize-auth-indexeddb',
    deviceRegistrationType: 'POS_DEVICE',
    loginPairingShortcuts: true,
    // storage
    secureStorage: 'keychain',
    localDatabase: 'sqlite-native',
    // payments
    manualCardEntry: 'payment-sheet',
    // BlockChyp LAN dispatch is iOS-only today (V5 payment-capabilities.ts, LOCAL_NETWORK).
    blockchypLocalNetwork: false,
    rendererBlockChypConsole: true,
    blockchypCloudProbe: true,
    // Stripe M2 reader sessions are iOS-only today (executor.ts:532-535).
    stripeM2: false,
    localTerminalPing: true,
    completedReportPlatform: 'ANDROID',
    desktopDispatchReconcile: false,
    // printing
    thermalPrinting: 'star-native',
    bluetoothPeripherals: true,
    desktopPrintDialog: false,
    escPosLanPrinting: false,
    escPosAttachedPrinting: false,
    orderAudio: 'native',
    // platform services
    osPermissionPrompts: true,
    otaUpdates: true,
    appVersionSource: 'capacitor-native',
    remoteVersionPolicy: true,
    deviceDiagnostics: true,
    batteryTelemetry: true,
    wifiManagement: 'android',
    inAppBrowser: 'capacitor-browser',
  },

  web: {
    // identity & auth
    nativeFirebaseAuth: false,
    firebaseWebApp: 'default',
    firebaseAuthInit: 'get-auth',
    deviceRegistrationType: 'POS_WEB_DEVICE',
    // A browser tab is not a dedicated register (today: `isNativePlatform()`).
    loginPairingShortcuts: false,
    // storage
    secureStorage: 'local-storage',
    localDatabase: 'dexie',
    // payments
    manualCardEntry: 'elements',
    blockchypLocalNetwork: false,
    rendererBlockChypConsole: true,
    blockchypCloudProbe: true,
    stripeM2: false,
    localTerminalPing: false,
    completedReportPlatform: 'WEB',
    desktopDispatchReconcile: false,
    // printing
    thermalPrinting: 'mock',
    bluetoothPeripherals: false,
    desktopPrintDialog: true,
    escPosLanPrinting: false,
    escPosAttachedPrinting: false,
    orderAudio: 'web',
    // platform services
    osPermissionPrompts: false,
    otaUpdates: false,
    appVersionSource: 'build',
    remoteVersionPolicy: false,
    deviceDiagnostics: false,
    batteryTelemetry: false,
    wifiManagement: 'none',
    inAppBrowser: 'window-open',
  },

  electron: {
    // identity & auth — the web SDK end to end (PLAN §2.2). The named
    // `web-services` app would leave the plugin's web impl calling getAuth()
    // on a [DEFAULT] app that never exists, i.e. `auth/no-app`.
    nativeFirebaseAuth: false,
    firebaseWebApp: 'default',
    // A custom-scheme origin is not `https:`, so keep the native rationale for
    // avoiding the GAPI iframe (auth-store.ts:379-381).
    firebaseAuthInit: 'initialize-auth-indexeddb',
    // A desktop register is a dedicated device, not a browser tab.
    deviceRegistrationType: 'POS_DEVICE',
    // The same environment-gated login pairing shortcuts as a native register
    // (owner request, Phase W). Never in a production build: that half of the
    // gate is `canRequestDemoAccess`, identical on every runtime.
    loginPairingShortcuts: true,
    // storage — Electron safeStorage in the main process, fail-closed (A3.4/Q4).
    secureStorage: 'electron-safe-storage',
    localDatabase: 'dexie',
    // payments — BlockChyp only on desktop (AMENDMENT A1/A2).
    manualCardEntry: 'none',
    // Phase W (Workstream A): terminals in BlockChyp local/LAN mode execute
    // from the main process over a gateway-resolved, private-IPv4-validated
    // route (`electron/main/services/blockchyp/service.ts`). Online only.
    blockchypLocalNetwork: true,
    // A1.3: no signing SDK client in the desktop renderer, admin tools included.
    rendererBlockChypConsole: false,
    blockchypCloudProbe: true,
    stripeM2: false,
    localTerminalPing: false,
    completedReportPlatform: 'DESKTOP',
    // T4-07: reconcile an unconfirmed main-process BlockChyp dispatch.
    desktopDispatchReconcile: true,
    // printing — Star LAN from the main process; the web mock is unreachable.
    thermalPrinting: 'star-electron-lan',
    bluetoothPeripherals: false,
    desktopPrintDialog: true,
    // Generic ESC/POS over raw TCP from the main process (beside Star LAN).
    escPosLanPrinting: true,
    // …and over installed Windows print queues / COM ports via the print bridge.
    escPosAttachedPrinting: true,
    orderAudio: 'web',
    // platform services
    osPermissionPrompts: false,
    otaUpdates: false,
    appVersionSource: 'platform-shell',
    remoteVersionPolicy: false,
    deviceDiagnostics: false,
    batteryTelemetry: false,
    wifiManagement: 'none',
    inAppBrowser: 'window-open',
  },
};
